var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const PROBE_TIMEOUT_MS = 1500;
export function probeRenderWorkerSupport(canvas, timeoutMs = PROBE_TIMEOUT_MS) {
    return __awaiter(this, void 0, void 0, function* () {
        const mainCanvasTransferSupported = !!canvas && typeof canvas.transferControlToOffscreen === 'function';
        const hasCanvas = !!canvas;
        if (canvas && !mainCanvasTransferSupported) {
            return {
                supported: false,
                reason: 'Canvas transferControlToOffscreen unavailable',
                offscreenCanvasSupported: typeof OffscreenCanvas !== 'undefined',
                canvasTransferSupported: false,
                webglSupported: false,
                workerCanvasTransferSupported: false,
            };
        }
        if (typeof Worker === 'undefined') {
            return {
                supported: false,
                reason: 'Worker unavailable',
                offscreenCanvasSupported: typeof OffscreenCanvas !== 'undefined',
                canvasTransferSupported: false,
                webglSupported: false,
                workerCanvasTransferSupported: false,
            };
        }
        if (typeof OffscreenCanvas === 'undefined') {
            return {
                supported: false,
                reason: 'OffscreenCanvas unavailable',
                offscreenCanvasSupported: false,
                canvasTransferSupported: false,
                webglSupported: false,
                workerCanvasTransferSupported: false,
            };
        }
        let worker = null;
        try {
            worker = new Worker(new URL('./RenderWorker.ts', import.meta.url), { type: 'module' });
        }
        catch (error) {
            return {
                supported: false,
                reason: 'Worker startup failed',
                offscreenCanvasSupported: true,
                canvasTransferSupported: false,
                webglSupported: false,
                workerCanvasTransferSupported: false,
            };
        }
        return new Promise((resolve) => {
            const shutdown = () => {
                if (!worker)
                    return;
                const message = { type: 'shutdown' };
                try {
                    worker.postMessage(message);
                }
                catch (error) {
                    // ignore
                }
                worker.terminate();
                worker = null;
            };
            const timeoutId = globalThis.setTimeout(() => {
                shutdown();
                resolve({
                    supported: false,
                    reason: 'probe timeout',
                    offscreenCanvasSupported: true,
                    canvasTransferSupported: false,
                    webglSupported: false,
                    workerCanvasTransferSupported: false,
                });
            }, timeoutMs);
            worker.onmessage = (event) => {
                if (!(event === null || event === void 0 ? void 0 : event.data) || event.data.type !== 'probe:ok')
                    return;
                globalThis.clearTimeout(timeoutId);
                const offscreenCanvasSupported = !!event.data.offscreenCanvasSupported;
                const webglSupported = !!event.data.webglSupported;
                const workerCanvasTransferSupported = !!event.data.canvasTransferSupported;
                const canvasTransferSupported = hasCanvas
                    ? mainCanvasTransferSupported && workerCanvasTransferSupported
                    : workerCanvasTransferSupported;
                const supported = offscreenCanvasSupported && webglSupported && canvasTransferSupported;
                const reason = offscreenCanvasSupported && !webglSupported
                    ? 'WebGL context unavailable'
                    : !canvasTransferSupported
                        ? 'OffscreenCanvas transfer failed'
                        : undefined;
                shutdown();
                resolve({
                    supported,
                    reason,
                    offscreenCanvasSupported,
                    canvasTransferSupported,
                    webglSupported,
                    workerCanvasTransferSupported,
                });
            };
            worker.onerror = () => {
                globalThis.clearTimeout(timeoutId);
                shutdown();
                resolve({
                    supported: false,
                    reason: 'worker error',
                    offscreenCanvasSupported: true,
                    canvasTransferSupported: false,
                    webglSupported: false,
                    workerCanvasTransferSupported: false,
                });
            };
            const probeCanvas = new OffscreenCanvas(1, 1);
            try {
                worker.postMessage({ type: 'probe', canvas: probeCanvas }, [probeCanvas]);
            }
            catch (error) {
                globalThis.clearTimeout(timeoutId);
                shutdown();
                resolve({
                    supported: false,
                    reason: 'OffscreenCanvas transfer failed',
                    offscreenCanvasSupported: true,
                    canvasTransferSupported: false,
                    webglSupported: false,
                    workerCanvasTransferSupported: false,
                });
            }
        });
    });
}
export function startRenderWorkerSession(config, timeoutMs = PROBE_TIMEOUT_MS, events = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        if (typeof Worker === 'undefined') {
            throw new Error('Worker unavailable');
        }
        if (typeof OffscreenCanvas === 'undefined') {
            throw new Error('OffscreenCanvas unavailable');
        }
        let worker = null;
        try {
            worker = new Worker(new URL('./RenderWorker.ts', import.meta.url), { type: 'module' });
        }
        catch (error) {
            throw new Error('Worker startup failed');
        }
        return new Promise((resolve, reject) => {
            const shutdown = () => {
                if (!worker)
                    return;
                const message = { type: 'shutdown' };
                try {
                    worker.postMessage(message);
                }
                catch (error) {
                    // ignore
                }
                worker.terminate();
                worker = null;
            };
            const timeoutId = globalThis.setTimeout(() => {
                shutdown();
                reject(new Error('render worker init timeout'));
            }, timeoutMs);
            let suspended = false;
            worker.onmessage = (event) => {
                var _a, _b;
                if (!(event === null || event === void 0 ? void 0 : event.data))
                    return;
                if (event.data.type === 'context:lost') {
                    (_a = events.onContextLost) === null || _a === void 0 ? void 0 : _a.call(events);
                    return;
                }
                if (event.data.type === 'context:restored') {
                    (_b = events.onContextRestored) === null || _b === void 0 ? void 0 : _b.call(events);
                    return;
                }
                if (event.data.type !== 'init:ok')
                    return;
                globalThis.clearTimeout(timeoutId);
                if (!event.data.webglReady) {
                    shutdown();
                    reject(new Error('render worker WebGL init failed'));
                    return;
                }
                resolve({
                    worker: worker,
                    shutdown,
                    resize: (nextConfig) => {
                        if (!worker)
                            return;
                        const message = { type: 'resize', config: nextConfig };
                        try {
                            worker.postMessage(message);
                        }
                        catch (error) {
                            // ignore
                        }
                    },
                    frame: (timestamp) => {
                        if (!worker)
                            return;
                        if (suspended)
                            return;
                        const message = { type: 'frame', timestamp };
                        try {
                            worker.postMessage(message);
                        }
                        catch (error) {
                            // ignore
                        }
                    },
                    setSuspended: (nextSuspended) => {
                        suspended = nextSuspended;
                        if (!worker)
                            return;
                        const message = { type: 'suspend', suspended };
                        try {
                            worker.postMessage(message);
                        }
                        catch (error) {
                            // ignore
                        }
                    },
                });
            };
            worker.onerror = () => {
                globalThis.clearTimeout(timeoutId);
                shutdown();
                reject(new Error('render worker init error'));
            };
            const initCanvas = new OffscreenCanvas(1, 1);
            const message = { type: 'init', config, canvas: initCanvas };
            try {
                worker.postMessage(message, [initCanvas]);
            }
            catch (error) {
                globalThis.clearTimeout(timeoutId);
                shutdown();
                reject(new Error('render worker init post failed'));
            }
        });
    });
}
